/* ==========================================================================
   Drive On — shared behaviour layer
   Progressive enhancement only. Every surface this touches renders and works
   with JavaScript disabled; this file adds motion and convenience, never
   content and never a control.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Reveal on scroll --------------------------------------------------
     The arming class is added by JS, so `.do-r` elements are only ever hidden
     when we know we can reveal them. Binding synchronously during parse once
     left every below-the-fold element permanently at opacity 0 — so this runs
     on DOMContentLoaded AND on load, and is idempotent. */

  var observer = null;

  function armReveal() {
    if (reduce) return;
    var root = document.documentElement;
    if (!('IntersectionObserver' in window)) return;

    if (!observer) {
      // threshold 0, not a fraction. A fractional threshold silently never
      // fires for an element that has zero area when it is first observed —
      // which is exactly what a media block looks like before its image has
      // decoded — and the block then stays hidden for the life of the page.
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.setAttribute('data-in', '');
          observer.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -40px 0px', threshold: 0 });
    }

    var nodes = document.querySelectorAll('.do-r:not([data-do-bound])');
    if (!nodes.length) return;
    root.classList.add('do-armed');
    nodes.forEach(function (n) {
      n.setAttribute('data-do-bound', '');
      // Anything already on screen when we bind reveals immediately rather
      // than waiting for a scroll that may never come on a short page.
      var r = n.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) { n.setAttribute('data-in', ''); return; }
      observer.observe(n);
    });
  }

  /* Stagger index for grids that opt in. */
  function indexStagger() {
    document.querySelectorAll('[data-do-stagger]:not([data-do-sx])').forEach(function (g) {
      g.setAttribute('data-do-sx', '');
      // Capped at 6 steps. Uncapped, a seventeen-question accordion delays its
      // last row by 1.2s, which reads as the page still loading rather than as
      // a stagger. Six steps is 420ms — long enough to be a cascade, short
      // enough that nobody waits for it.
      Array.prototype.forEach.call(g.children, function (c, i) {
        c.style.setProperty('--do-i', String(i > 6 ? 6 : i));
      });
    });
  }

  /* ---- Marquee: duplicate the track so the -50% keyframe seams ----------- */
  function seamMarquees() {
    document.querySelectorAll('.do-marquee__t:not([data-do-seamed])').forEach(function (t) {
      t.setAttribute('data-do-seamed', '');
      if (reduce) return;
      t.innerHTML = t.innerHTML + t.innerHTML;
    });
  }

  /* ---- Counters ----------------------------------------------------------
     Used only for values that are literally in the DOM already, so the number
     is correct before, during and after the animation. */
  function countUp(el) {
    var to = parseFloat(el.getAttribute('data-do-count'));
    if (isNaN(to) || reduce) return;
    var dp = parseInt(el.getAttribute('data-do-dp') || '0', 10);
    var pre = el.getAttribute('data-do-pre') || '';
    var post = el.getAttribute('data-do-post') || '';
    var t0 = null, dur = 900;
    function step(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = 1 - Math.pow(1 - p, 3);
      el.textContent = pre + (to * e).toFixed(dp) + post;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function armCounters() {
    if (reduce || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        countUp(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.6 });
    document.querySelectorAll('[data-do-count]:not([data-do-cbound])').forEach(function (n) {
      n.setAttribute('data-do-cbound', '');
      io.observe(n);
    });
  }

  /* ---- Parallax ----------------------------------------------------------
     rAF-throttled, transform-only, disabled entirely under reduced motion. */
  var pxNodes = [];
  var pxTicking = false;

  function pxRead() {
    pxNodes.forEach(function (n) {
      var r = n.el.getBoundingClientRect();
      var vh = window.innerHeight;
      if (r.bottom < -200 || r.top > vh + 200) return;
      var mid = r.top + r.height / 2;
      var d = (mid - vh / 2) / vh;           // -1 .. 1 through the viewport
      n.el.style.transform = 'translate3d(0,' + (d * n.amt).toFixed(2) + 'px,0)';
    });
    pxTicking = false;
  }

  function onScroll() {
    if (pxTicking) return;
    pxTicking = true;
    requestAnimationFrame(pxRead);
  }

  function armParallax() {
    if (reduce) return;
    document.querySelectorAll('[data-do-parallax]:not([data-do-pbound])').forEach(function (n) {
      n.setAttribute('data-do-pbound', '');
      var amt = parseFloat(n.getAttribute('data-do-parallax')) || 24;
      n.style.willChange = 'transform';
      pxNodes.push({ el: n, amt: amt });
    });
    if (pxNodes.length) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---- Header state ------------------------------------------------------ */
  function armHeader() {
    var h = document.querySelector('[data-do-header]');
    if (!h) return;
    var last = 0;
    function upd() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      h.toggleAttribute('data-scrolled', y > 12);
      if (h.hasAttribute('data-do-header-hide')) {
        h.toggleAttribute('data-tucked', y > last && y > 220);
      }
      last = y;
    }
    window.addEventListener('scroll', function () { requestAnimationFrame(upd); }, { passive: true });
    upd();
  }

  /* Safety net. Images finish decoding after the observer was armed, elements
     resize, and a browser can restore a scroll position mid-page. Anything
     already on screen is revealed outright rather than waiting for a scroll
     event that may never come. */
  function sweepVisible() {
    if (reduce) return;
    var vh = window.innerHeight;
    document.querySelectorAll('.do-r:not([data-in])').forEach(function (n) {
      var r = n.getBoundingClientRect();
      if (r.top < vh + 80 && r.bottom > -80) {
        n.setAttribute('data-in', '');
        if (observer) observer.unobserve(n);
      }
    });
  }

  /* --- Quick add ---------------------------------------------------------
     Progressive enhancement, not a dependency. Every size in the grid is a
     real <form> posting to /cart/add, so with JavaScript off a click is a
     normal form submit that lands on the cart page and works. What this adds
     is the thing that makes it feel like a shop rather than a form: the page
     does not navigate, the button confirms, and the bag count moves.

     Errors deliberately fall back to a real submit rather than showing a
     message. If the fetch failed we do not know why — sold out between render
     and click, a network drop, a redirect — and the server's own response to
     a normal submit is more trustworthy than a guess rendered in the grid. */
  function armQuickAdd() {
    document.querySelectorAll('[data-do-qa]:not([data-do-qa-bound])').forEach(function (wrap) {
      wrap.setAttribute('data-do-qa-bound', '');
      wrap.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-do-qa-form]');
        if (!form || !window.fetch) return;
        e.preventDefault();
        var btn = form.querySelector('button');
        if (!btn || btn.disabled) return;
        var label = btn.textContent;
        btn.disabled = true;

        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/javascript' },
          credentials: 'same-origin'
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          btn.setAttribute('data-do-added', '');
          btn.textContent = '\u2713';
          refreshBagCount();
          // The whole point of a quick add is that the shopper sees what they
          // just did without losing the grid they were browsing.
          refreshDrawer(true);
          window.setTimeout(function () {
            btn.removeAttribute('data-do-added');
            btn.textContent = label;
            btn.disabled = false;
          }, 1400);
        }).catch(function () {
          // Let the browser do it properly.
          btn.disabled = false;
          form.submit();
        });
      });
    });
  }

  /* The header bag count is the only cart chrome on the page, so it is the only
     thing that has to stay honest after a quick add. One cheap JSON read. */
  function refreshBagCount() {
    if (!window.fetch) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-do-cart-count]').forEach(function (n) {
          n.textContent = cart.item_count;
          n.toggleAttribute('hidden', cart.item_count === 0);
        });
        document.querySelectorAll('[data-do-ship-bar]').forEach(function (bar) {
          paintShipBar(bar, cart.total_price);
        });
      }).catch(function () { /* the count is cosmetic; never break the page for it */ });
  }

  /* --- Free-shipping progress -------------------------------------------
     The one merchandising mechanic worth having on a thirteen-product store:
     with no catalogue breadth to cross-sell, order value is the lever, and the
     threshold already exists ($70). Reads its numbers from data attributes so
     the copy and the threshold live in Liquid where a merchant can edit them,
     not in this file. */
  function paintShipBar(bar, cents) {
    var goal = parseInt(bar.getAttribute('data-goal') || '0', 10);
    if (!goal) return;
    var now = typeof cents === 'number' ? cents : parseInt(bar.getAttribute('data-now') || '0', 10);
    var pct = Math.max(0, Math.min(100, Math.round((now / goal) * 100)));
    var fill = bar.querySelector('[data-do-ship-fill]');
    var msg = bar.querySelector('[data-do-ship-msg]');
    if (fill) fill.style.width = pct + '%';
    bar.setAttribute('aria-valuenow', String(pct));
    if (!msg) return;
    if (now >= goal) {
      msg.textContent = bar.getAttribute('data-done') || '';
    } else {
      var left = ((goal - now) / 100).toFixed(2).replace(/\.00$/, '');
      // '[amount]', not a Liquid brace: Shopify's lexer cannot terminate an
      // output tag that contains one, so the placeholder had to be brace-free.
      msg.textContent = (bar.getAttribute('data-away') || '').replace('[amount]', '$' + left);
    }
  }

  function armShipBars() {
    document.querySelectorAll('[data-do-ship-bar]').forEach(function (bar) {
      paintShipBar(bar);
    });
  }

  /* --- Cart drawer -------------------------------------------------------
     Opening is cheap; keeping it TRUE is the work. Every mutation re-fetches
     the drawer section from Shopify's Section Rendering API and swaps its body
     in, so prices, discounts and the free-shipping message are always the ones
     Liquid rendered rather than a JavaScript approximation of them.

     Focus handling is deliberate and worth reading before changing:
     the element that opened the drawer is remembered and focused again on
     close, focus moves into the panel on open, and Tab is trapped between the
     first and last focusable node while it is open. A drawer you can tab out
     of, into a page you cannot see, is the most common accessibility failure
     in this pattern. */
  // NB: `data-do-drawer` is taken — the header's burger menu uses it. The cart
  // drawer is namespaced `data-do-cart-*` so a querySelector for one never
  // returns the other, which is exactly what happened the first time.
  var drawerOpener = null;

  function drawerEl() { return document.querySelector('[data-do-cart-drawer]'); }

  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'),
      function (n) { return n.offsetParent !== null; });
  }

  function openDrawer() {
    var d = drawerEl();
    if (!d) return;
    drawerOpener = document.activeElement;
    d.hidden = false;
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    // Two frames: one to paint the closed state, one to start the transition.
    // Without the gap the panel is simply there and the slide never runs.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        d.setAttribute('data-open', '');
        var panel = d.querySelector('[data-do-cart-panel]');
        var f = panel && focusables(panel);
        if (f && f.length) f[0].focus();
      });
    });
  }

  function closeDrawer() {
    var d = drawerEl();
    if (!d || d.hidden) return;
    d.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    d.setAttribute('aria-hidden', 'true');
    var done = function () { d.hidden = true; };
    if (reduce) { done(); } else { window.setTimeout(done, 320); }
    if (drawerOpener && drawerOpener.focus) drawerOpener.focus();
    drawerOpener = null;
  }

  /* Re-render from the source of truth. `?sections=` returns the section's own
     HTML exactly as Liquid would have rendered it on a full page load. */
  function refreshDrawer(thenOpen) {
    if (!window.fetch) { if (thenOpen) window.location.href = '/cart'; return; }
    var d = drawerEl();
    var id = d && d.closest('[id^="shopify-section-"]');
    var name = id ? id.id.replace('shopify-section-', '') : 'cart-drawer';
    return fetch('/?sections=' + encodeURIComponent(name), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[name];
        if (!html || !id) return;
        var wrap = document.createElement('div');
        wrap.innerHTML = html;
        var fresh = wrap.querySelector('[data-do-cart-drawer]');
        var live = drawerEl();
        if (!fresh || !live) return;
        var wasOpen = live.hasAttribute('data-open');
        live.innerHTML = fresh.innerHTML;
        if (wasOpen) live.setAttribute('data-open', '');
        armShipBars();
        // Every cart write lands here, so this is the one place the undo bar can
        // be repainted — the innerHTML swap above has just destroyed the old one.
        paintUndo();
        if (thenOpen) openDrawer();
      }).catch(function () {
        if (thenOpen) window.location.href = '/cart';
      });
  }

  function armDrawer() {
    if (document.documentElement.hasAttribute('data-do-drawer-bound')) return;
    document.documentElement.setAttribute('data-do-drawer-bound', '');

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-do-cart-close]')) { e.preventDefault(); closeDrawer(); return; }
      // The header bag link opens the drawer instead of navigating — but only
      // when the drawer exists and fetch is available, so the link keeps
      // working as a link everywhere else.
      var bag = e.target.closest('[data-do-bag]');
      if (bag && drawerEl() && window.fetch) { e.preventDefault(); refreshDrawer(true); }
    });

    document.addEventListener('keydown', function (e) {
      var d = drawerEl();
      if (!d || d.hidden) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      var panel = d.querySelector('[data-do-cart-panel]');
      var f = panel && focusables(panel);
      if (!f || !f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // Quantity and remove inside the drawer, intercepted and re-rendered.
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-do-cart-form]');
      if (!form || !window.fetch) return;
      e.preventDefault();
      // Read what is about to be destroyed BEFORE the post: once /cart/change
      // has run, the line is gone and there is nothing left to ask for its
      // variant or quantity.
      if (form.hasAttribute('data-do-remove')) {
        lastRemoved = {
          id: form.getAttribute('data-do-variant'),
          qty: parseInt(form.getAttribute('data-do-qty'), 10) || 1,
          title: form.getAttribute('data-do-title') || 'That piece',
          at: Date.now()
        };
      } else {
        lastRemoved = null;
      }
      fetch(form.action, {
        method: 'POST', body: new FormData(form),
        headers: { Accept: 'application/javascript' }, credentials: 'same-origin'
      }).then(function () { refreshBagCount(); return refreshDrawer(false); })
        .catch(function () { form.submit(); });
    });

    document.addEventListener('click', function (e) {
      var u = e.target.closest('[data-do-undo]');
      if (!u || !lastRemoved) return;
      e.preventDefault();
      var restore = lastRemoved;
      lastRemoved = null;
      u.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(restore.id), quantity: restore.qty }] })
      }).then(function () { refreshBagCount(); return refreshDrawer(false); })
        .catch(function () { window.location.href = '/cart'; });
    });
  }

  /* --- Undo a removal ------------------------------------------------------
     Holds the last removed line for as long as UNDO_MS, and paints it into the
     slot the section renders. The slot has to come from Liquid because
     refreshDrawer() replaces the drawer's innerHTML after every cart write.

     ponytail: restores variant id + quantity only. Line item properties — the
     `_do_set` grouping in particular — are not carried back, so undoing a set
     piece returns it as a loose line. Capture `item.properties` into the form's
     dataset and post them through if sets ever ship for real. */
  var lastRemoved = null;
  var UNDO_MS = 20000;

  function paintUndo() {
    var slot = document.querySelector('[data-do-undo-slot]');
    if (!slot) return;
    if (!lastRemoved || Date.now() - lastRemoved.at > UNDO_MS) {
      slot.textContent = '';
      return;
    }
    // textContent for the title, so a product name containing markup cannot
    // become markup here.
    var msg = document.createElement('span');
    msg.textContent = 'Removed ' + lastRemoved.title + '.';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dw__undob';
    btn.setAttribute('data-do-undo', '');
    btn.textContent = 'Undo';
    slot.textContent = '';
    slot.appendChild(msg);
    slot.appendChild(btn);
  }

  /* --- Cart page quantity stepper ----------------------------------------
     The cart page posts `updates[]`, which is positional and works perfectly
     without any of this. All the stepper does is nudge the same input and
     submit the same form, so there is exactly one quantity control on the page
     wearing two faces. The buttons are printed hidden and revealed by the flag
     this function sets, because a button that cannot work should not be drawn.

     Submitting on every click rather than debouncing: a cart that shows a
     number the server has not agreed to yet is a cart that lies, and the whole
     page is one form post away from being right. */
  function armQtySteppers() {
    var wraps = document.querySelectorAll('[data-do-qty]:not([data-do-qty-b])');
    if (!wraps.length) return;
    document.documentElement.setAttribute('data-do-qty-bound', '');
    wraps.forEach(function (wrap) {
      wrap.setAttribute('data-do-qty-b', '');
      wrap.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-do-qty-step]');
        if (!btn) return;
        var input = wrap.querySelector('[data-do-qty-input]');
        if (!input) return;
        var step = parseInt(btn.getAttribute('data-do-qty-step'), 10) || 0;
        var next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
        if (next === (parseInt(input.value, 10) || 0)) return;
        input.value = next;
        var form = input.form;
        if (!form) return;
        // name="update" is what tells Shopify's /cart handler to apply the
        // updates[] array rather than treating this as a plain navigation.
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'update';
        hidden.value = '';
        form.appendChild(hidden);
        form.submit();
      });
    });
  }

  /* --- Complete the set --------------------------------------------------
     Each row of the set is already a working <form> that posts one variant, so
     nothing here is load-bearing. Two enhancements sit on top:

     · a row submit becomes a fetch, so the shopper stays on the product;
     · one request adds every row at once, via /cart/add.js with an `items`
       array. That endpoint takes several lines in a single call, which matters
       — three sequential adds is three round trips and three chances for one to
       fail halfway and leave a half-built set in the bag.

     The add-all button is printed hidden and revealed here, because it is the
     only control in the section that cannot fall back to a plain submit. */
  function setRows(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-do-set-row]'));
  }

  /* A row counts as picked when its box is ticked, and ALSO when it has no box
     at all — a one-row set is never given one, and treating "no control" as
     "not chosen" would silently empty the add-all button on those pages. */
  function setPicked(form) {
    var box = form.querySelector('[data-do-set-pick]');
    return !box || box.checked;
  }

  function setLineFor(form, setName) {
    var sel = form.querySelector('[data-do-set-sel]');
    if (!sel || !sel.value) return null;
    var opt = sel.options[sel.selectedIndex];
    if (opt && opt.disabled) return null;
    var line = { id: Number(sel.value), quantity: 1 };
    if (setName) line.properties = { _do_set: setName };
    return line;
  }

  function flashAdded(btn, word) {
    if (!btn) return;
    var was = btn.textContent;
    btn.setAttribute('data-do-added', '');
    btn.textContent = word || 'Added';
    window.setTimeout(function () {
      btn.removeAttribute('data-do-added');
      btn.textContent = was;
      btn.disabled = false;
    }, 1500);
  }

  function armSet() {
    document.querySelectorAll('[data-do-set]:not([data-do-set-bound])').forEach(function (root) {
      root.setAttribute('data-do-set-bound', '');
      var setName = root.getAttribute('data-set-name') || '';
      var added = root.getAttribute('data-added') || 'Added';

      /* Reveal the piece pickers. Two conditions, both real: without fetch the
         add-all button never appears and the boxes would read nothing, and with
         one row there is no choice to make. */
      if (window.fetch && setRows(root).length > 1) {
        root.setAttribute('data-do-set-ready', '');
      }

      root.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-do-set-row]');
        if (!form || !window.fetch) return;
        e.preventDefault();
        var btn = form.querySelector('[data-do-set-add]');
        var line = setLineFor(form, setName);
        if (!line) return;
        if (btn) btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [line] })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          flashAdded(btn, added);
          refreshBagCount();
          refreshDrawer(true);
        }).catch(function () {
          if (btn) btn.disabled = false;
          form.submit();
        });
      });
    });

    // The section-level add-all lives outside [data-do-set], next to the total.
    document.querySelectorAll('[data-do-set-all]:not([data-do-set-all-bound])').forEach(function (btn) {
      btn.setAttribute('data-do-set-all-bound', '');
      var scope = btn.closest('[id^="shopify-section-"]') || document;
      var root = scope.querySelector('[data-do-set]');
      if (!root || !window.fetch) return;
      btn.setAttribute('data-do-set-ready', '');
      var label = btn.querySelector('[data-do-set-all-label]');
      var word = label ? label.textContent : btn.textContent;
      var setName = root.getAttribute('data-set-name') || '';
      var added = root.getAttribute('data-added') || 'Added';

      /* The button states how many pieces it is about to add, and stops being
         clickable when that is none. The count is a number, not money — see the
         note in do-set.liquid for why no subtotal is computed here. */
      var someTpl = btn.getAttribute('data-some-label') || '';
      function syncLabel() {
        var n = setRows(root).filter(setPicked).length;
        btn.disabled = n === 0;
        if (!label) return;
        label.textContent = (n === setRows(root).length || !someTpl)
          ? word
          : someTpl.replace('{count}', String(n));
      }
      root.addEventListener('change', function (e) {
        if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-do-set-pick')) syncLabel();
      });
      syncLabel();

      btn.addEventListener('click', function () {
        var lines = setRows(root).filter(setPicked)
                                 .map(function (f) { return setLineFor(f, setName); })
                                 .filter(Boolean);
        if (!lines.length) return;
        btn.disabled = true;
        if (label) label.textContent = '…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: lines })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          if (label) label.textContent = added;
          refreshBagCount();
          refreshDrawer(true);
          window.setTimeout(function () {
            // Back through syncLabel, not straight to `word`: the shopper may
            // have unticked something while the confirmation was showing, and
            // restoring "Add all to bag" would then be a lie.
            syncLabel();
          }, 1600);
        }).catch(function () {
          // No silent failure and no half-built set: send them to the cart,
          // where whatever did land is visible and editable.
          window.location.href = '/cart';
        });
      });
    });
  }

  /* --- Quick view ---------------------------------------------------------
     The grid's circular "+" opens a stripped product page in a dialog instead
     of navigating. It is built from the Section Rendering API — the real PDP
     section, rendered by Liquid, for the real product — so prices, sizes and
     stock cannot drift from the page it stands in for. Nothing about it is
     required: the control is a real link to the product, and this only
     intercepts the click when fetch and the modal are both available. */
  var qvOpener = null;

  function qvEl() { return document.querySelector('[data-do-qv]'); }

  function closeQuickView() {
    var m = qvEl();
    if (!m || m.hidden) return;
    m.removeAttribute('data-open');
    m.removeAttribute('data-variant');
    m.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    var done = function () { m.hidden = true; };
    if (reduce) { done(); } else { window.setTimeout(done, 280); }
    if (qvOpener && qvOpener.focus) qvOpener.focus();
    qvOpener = null;
  }

  /* One dialog host, two presentations. `variant` is written onto the host as
     `data-variant` and CSS does the rest: unset is the centred quick view of
     04/03, "sheet" is the bottom sheet of 09. A second host element would mean
     a second focus trap, a second Escape handler and a second scroll lock, all
     of which would have to stay in step with this one forever. */
  function openQuickView(url, opener, sectionName, variant) {
    var m = qvEl();
    if (!m || !window.fetch) return false;
    var body = m.querySelector('[data-do-qv-body]');
    var name = sectionName || m.getAttribute('data-card-section') || 'do-quick-view-card';
    if (!body) return false;
    qvOpener = opener || document.activeElement;
    body.setAttribute('data-loading', '');
    m.hidden = false;
    if (variant) m.setAttribute('data-variant', variant);
    else m.removeAttribute('data-variant');
    m.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { m.setAttribute('data-open', ''); });
    });

    // `?section_id=` renders ONE section file standalone, in this URL's context,
    // and returns raw HTML. `?sections=` is the other endpoint — it returns JSON
    // but only for sections the current template actually contains, which a
    // quick-view card deliberately is not.
    // The cache key carries the SECTION as well as the URL. Two presentations
    // now fetch the same product URL, and a bare-URL key would serve the quick
    // view's markup into the sheet — or the reverse — depending only on which
    // one the shopper happened to open first.
    var key = name + '|' + url;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var pending = qvCache[key] || fetch(
      url + sep + 'section_id=' + encodeURIComponent(name), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('section ' + r.status);
        return r.text();
      });
    qvCache[key] = pending;
    pending
      .then(function (html) {
        if (!html) throw new Error('empty');
        var wrap = document.createElement('div');
        wrap.innerHTML = html;
        // The endpoint wraps the section in its own #shopify-section- div; take
        // the marked content so the modal does not inherit a second positioning
        // context, and treat a missing marker as a failure rather than pasting
        // whatever came back.
        var inner = wrap.querySelector('[data-do-qv-content]');
        if (!inner) throw new Error('no card');
        body.innerHTML = inner.innerHTML;
        body.removeAttribute('data-loading');
        armBuy();
        armReveal();
        sweepVisible();
        var f = focusables(body);
        if (f.length) f[0].focus();
        else {
          // Same trap as the zoom viewer: the scrim also carries the close hook
          // and comes first, so this has to name the button.
          var close = m.querySelector('button[data-do-qv-close]');
          if (close) close.focus();
        }
      }).catch(function () {
        // A quick view that cannot load is a link that should have navigated.
        // Drop the cached failure so a retry is not permanently poisoned.
        delete qvCache[key];
        closeQuickView();
        window.location.href = url;
      });
    return true;
  }

  function armQuickView() {
    if (document.documentElement.hasAttribute('data-do-qv-bound')) return;
    document.documentElement.setAttribute('data-do-qv-bound', '');

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-do-qv-close]')) { e.preventDefault(); closeQuickView(); return; }
      var trigger = e.target.closest('[data-do-qa-open],[data-do-qv-open]');
      if (!trigger) return;
      var sheet = trigger.hasAttribute('data-do-qa-open');
      var url = trigger.getAttribute(sheet ? 'data-do-qa-open' : 'data-do-qv-open')
             || trigger.getAttribute('href');
      if (!url) return;
      if (openQuickView(url, trigger,
                        sheet ? 'do-quick-add-card' : null,
                        sheet ? 'sheet' : null)) e.preventDefault();
    });

    document.addEventListener('keydown', function (e) {
      var m = qvEl();
      if (!m || m.hidden) return;
      if (e.key === 'Escape') { closeQuickView(); return; }
      if (e.key !== 'Tab') return;
      var panel = m.querySelector('[data-do-qv-panel]');
      var f = panel && focusables(panel);
      if (!f || !f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* --- Product forms, intercepted ----------------------------------------
     Every add-to-bag form on the store — the PDP's, the quick view's — is a
     real POST to /cart/add that works on its own. Where a drawer exists and
     fetch is available, this turns that navigation into an in-place add: the
     shopper stays on the page they were reading and the bag slides in with
     what they just chose in it.

     One handler at the document level rather than one per form, because the
     quick view's form does not exist until its card is fetched, and rebinding
     after every fetch is how a second listener ends up double-adding.

     Failure falls back to a real submit. If the fetch failed we do not know why
     — sold out between render and click, a network drop — and the server's own
     response to a normal post is more trustworthy than a guess. */
  function armProductForms() {
    if (document.documentElement.hasAttribute('data-do-pf')) return;
    document.documentElement.setAttribute('data-do-pf', '');

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.getAttribute('action') !== '/cart/add') return;
      // The grid's quick add and the set rows have their own handlers and their
      // own confirmation language; do not intercept them twice.
      if (form.closest('[data-do-qa]') || form.matches('[data-do-set-row]')) return;
      if (!window.fetch || !drawerEl()) return;
      var id = form.querySelector('[name="id"]:checked') || form.querySelector('[name="id"]');
      if (!id || !id.value) return;

      e.preventDefault();
      var btn = form.querySelector('[data-do-buy-btn]') || form.querySelector('button[type="submit"]');
      var was = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.setAttribute('data-do-busy', ''); }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(id.value), quantity: 1 }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        if (btn) {
          btn.removeAttribute('data-do-busy');
          btn.setAttribute('data-do-added', '');
          btn.textContent = form.getAttribute('data-added') || 'Added';
        }
        refreshBagCount();
        // Close the quick view first: a dialog and a drawer open at once is two
        // focus traps fighting over the same Tab key.
        if (form.closest('[data-do-qv]')) closeQuickView();
        refreshDrawer(true);
        window.setTimeout(function () {
          if (!btn) return;
          btn.removeAttribute('data-do-added');
          btn.textContent = was;
          btn.disabled = false;
        }, 1600);
      }).catch(function () {
        if (btn) { btn.disabled = false; btn.removeAttribute('data-do-busy'); }
        form.submit();
      });
    });
  }

  /* Prefetch a quick-view card on hover. The dialog already opens instantly and
     fills a moment later; this closes that moment on a pointer device without
     costing a touch device anything, because there is no hover to trigger it. */
  var qvCache = {};

  function armQuickViewPrefetch() {
    if (!window.fetch) return;
    document.addEventListener('pointerenter', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var trigger = t.closest('[data-do-qv-open]');
      if (!trigger) return;
      var url = trigger.getAttribute('data-do-qv-open');
      var m = qvEl();
      if (!url || !m) return;
      var name = m.getAttribute('data-card-section') || 'do-quick-view-card';
      if (qvCache[name + '|' + url]) return;
      qvCache[name + '|' + url] = fetch(url + (url.indexOf('?') === -1 ? '?' : '&') +
                           'section_id=' + encodeURIComponent(name),
                           { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .catch(function () { return null; });
    }, true);
  }

  /* --- Zoom, in place -----------------------------------------------------
     Modelled on what the reference storefront actually does, which is NOT a
     lightbox: click the frame and the photograph scales up inside its own box,
     the mouse moves around inside it at pointer frame rate, and clicking again
     puts it back. No dialog opens, nothing covers the page, and the buy column
     stays where it was — which is the whole point, because a shopper looking
     closely at cloth is a shopper about to choose a size.

     The mechanism is `transform-origin`, not `translate`. Setting the origin to
     the cursor's position as a percentage of the frame and then scaling about
     it keeps that exact point of the picture under the cursor — so panning is
     one property write per pointer event, no arithmetic, no clamping, and it
     cannot be dragged out of its own box. `translate` needs the inverse
     calculation plus bounds maths on every frame and drifts at the edges.

     Cost per move: one style write inside a rAF. The transition applies to
     `transform` only, so the scale eases in and out while the origin tracks the
     pointer instantly — an eased origin lags the cursor and feels like syrup.

     Touch has no cursor to follow, so a coarse pointer drags the origin instead:
     the finger moves and the picture moves with it, which is the direction a
     finger expects. */
  var ZOOM_K = 2.4;

  function zoomState(frame) {
    return frame.hasAttribute('data-do-zoomed');
  }

  function zoomSetOrigin(frame, clientX, clientY) {
    var img = frame.querySelector('img');
    if (!img) return;
    var r = frame.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var x = ((clientX - r.left) / r.width) * 100;
    var y = ((clientY - r.top) / r.height) * 100;
    img.style.transformOrigin =
      Math.max(0, Math.min(100, x)).toFixed(2) + '% ' +
      Math.max(0, Math.min(100, y)).toFixed(2) + '%';
  }

  function zoomIn(frame, clientX, clientY) {
    var img = frame.querySelector('img');
    if (!img || reduce) return;
    // Swap in the larger rendition the moment it is wanted, not on page load.
    // A 2400px file per frame downloaded up front is the whole reason galleries
    // feel heavy; downloaded on the click that asks for it, it costs nothing
    // until someone is genuinely looking.
    var big = frame.getAttribute('data-do-zoom-src');
    if (big && img.getAttribute('data-do-zoom-swapped') === null) {
      var pre = new Image();
      pre.onload = function () {
        img.src = big;
        img.removeAttribute('srcset');
        img.setAttribute('data-do-zoom-swapped', '');
      };
      pre.src = big;
    }
    zoomSetOrigin(frame, clientX, clientY);
    frame.setAttribute('data-do-zoomed', '');
    img.style.transform = 'scale(' + ZOOM_K + ')';
  }

  function zoomOut(frame) {
    var img = frame.querySelector('img');
    frame.removeAttribute('data-do-zoomed');
    if (img) img.style.transform = '';
  }

  function zoomOutAll(except) {
    document.querySelectorAll('[data-do-zoomed]').forEach(function (f) {
      if (f !== except) zoomOut(f);
    });
  }

  function armZoom() {
    if (document.documentElement.hasAttribute('data-do-zoom-bound')) return;
    if (!document.querySelector('[data-do-zoom]')) return;
    document.documentElement.setAttribute('data-do-zoom-bound', '');

    var moveFrame = null, moveX = 0, moveY = 0, ticking = false;

    function flush() {
      ticking = false;
      if (moveFrame && zoomState(moveFrame)) zoomSetOrigin(moveFrame, moveX, moveY);
    }

    document.addEventListener('click', function (e) {
      var frame = e.target.closest('[data-do-zoom]');
      if (!frame) { zoomOutAll(null); return; }
      // The button is an affordance, not a second mechanism: it toggles the
      // frame it sits in, exactly as clicking the picture does.
      e.preventDefault();
      if (zoomState(frame)) { zoomOut(frame); return; }
      zoomOutAll(frame);
      zoomIn(frame, e.clientX, e.clientY);
    });

    document.addEventListener('pointermove', function (e) {
      var frame = e.target.closest('[data-do-zoom][data-do-zoomed]');
      if (!frame) return;
      // A coarse pointer is a finger: it drags the picture rather than pointing
      // at it, so the origin follows the touch directly and the image moves the
      // way the finger moves.
      moveFrame = frame; moveX = e.clientX; moveY = e.clientY;
      if (e.pointerType === 'touch') e.preventDefault();
      if (!ticking) { ticking = true; requestAnimationFrame(flush); }
    }, { passive: false });

    // Leaving the frame with the pointer resets it. Leaving a photograph
    // magnified behind you as you scroll away is a state nobody asked for and
    // nobody can see they are in.
    document.addEventListener('pointerleave', function (e) {
      var frame = e.target.closest && e.target.closest('[data-do-zoom][data-do-zoomed]');
      if (frame && e.pointerType !== 'touch') zoomOut(frame);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { zoomOutAll(null); return; }
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var frame = document.activeElement && document.activeElement.closest
        ? document.activeElement.closest('[data-do-zoom]') : null;
      if (!frame) return;
      e.preventDefault();
      if (zoomState(frame)) { zoomOut(frame); return; }
      // Keyboard has no pointer position, so it zooms about the centre.
      var r = frame.getBoundingClientRect();
      zoomOutAll(frame);
      zoomIn(frame, r.left + r.width / 2, r.top + r.height / 2);
    });

    // A zoomed frame inside a horizontally scrolling strip must not stay
    // magnified while the strip moves under it.
    window.addEventListener('scroll', function () { zoomOutAll(null); }, { passive: true });
  }

  /* --- Cross-document transitions ----------------------------------------
     Two lines of CSS in driveon.css do the actual work; this only opts the
     document in and tags the frame that should carry across. The API degrades
     to a plain navigation everywhere it is not supported, which is most
     browsers today — nothing here is load-bearing. */
  function armTransitions() {
    if (reduce) return;
    if (document.documentElement.hasAttribute('data-do-vt')) return;
    document.documentElement.setAttribute('data-do-vt', '');

    // Name the frame the shopper actually clicked, so the browser has one
    // element to match on the far side rather than guessing between twelve
    // cards that all look alike.
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      if (!a) return;
      var card = a.closest('[data-do-vt-name]');
      if (!card) return;
      document.querySelectorAll('[data-do-vt-active]').forEach(function (n) {
        n.removeAttribute('data-do-vt-active');
        n.style.viewTransitionName = '';
      });
      card.setAttribute('data-do-vt-active', '');
      var media = card.querySelector('[data-do-vt-media]') || card;
      media.style.viewTransitionName = 'do-frame';
    }, true);
  }

  /* --- Multi-state buy button --------------------------------------------
     Liquid ships this working: first available size pre-checked, button says
     "Add to cart", and a no-JS submit posts a real variant id. That order
     matters — the enhancement must never be what makes the form valid.

     What JS adds is the deliberate friction that both reference PDPs have:
     clear the reflex pre-selection, so the shopper has to name a size, and
     then keep the button honest about what that size actually is. */
  function armBuy() {
    document.querySelectorAll('[data-do-buy]:not([data-do-buy-bound])').forEach(function (box) {
      box.setAttribute('data-do-buy-bound', '');
      var form = box.closest('form');
      var btn = box.querySelector('[data-do-buy-btn]');
      if (!form || !btn) return;
      var radios = form.querySelectorAll('input[type="radio"][name="id"]');
      if (!radios.length) return;                 // select fallback: leave alone

      var copy = {
        select: box.getAttribute('data-select') || 'Select a size',
        add: box.getAttribute('data-add') || 'Add to cart',
        soldout: box.getAttribute('data-soldout') || 'Sold out'
      };

      function paint() {
        var chosen = form.querySelector('input[type="radio"][name="id"]:checked');
        if (!chosen) {
          btn.textContent = copy.select;
          btn.setAttribute('aria-disabled', 'true');
          // `data-state` so the stylesheet can tell WAITING from CLOSED. A
          // button that is disabled because the shopper has not chosen yet
          // should look like a control about to light up; one that is disabled
          // because the size is gone should look shut.
          btn.setAttribute('data-state', 'select');
          btn.disabled = true;
        } else if (chosen.disabled) {
          btn.textContent = copy.soldout;
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-state', 'soldout');
          btn.disabled = true;
        } else {
          // The quick-add sheet's wireframe puts the price ON the button
          // ("Add to bag — $138.00") because it is the only price the shopper
          // sees after choosing a size. Opt-in via `data-price-in-cta` so the
          // product page and the quick view, which both carry a price of their
          // own directly above the button, are not given a second one.
          var p = box.hasAttribute('data-price-in-cta') && chosen.getAttribute('data-price');
          btn.textContent = p ? copy.add + ' — ' + p : copy.add;
          btn.removeAttribute('aria-disabled');
          btn.setAttribute('data-state', 'add');
          btn.disabled = false;
        }
      }

      radios.forEach(function (r) {
        r.checked = false;
        r.addEventListener('change', paint);
      });
      paint();
    });
  }

  /* --- Mobile sticky purchase bar ----------------------------------------
     08_mobile_pdp: the buy action follows the shopper down the page, but only
     once they have scrolled past the real one. Three jobs and nothing else:

       reveal   watch the real buy button; show the bar when it is off screen
       mirror   copy that button's label and the chosen size into the bar
       forward  send a tap on the bar to the real form, or — if no size has
                been chosen yet — to the size row, focused

     The forwarding is the whole design. The bar owns no variant state, so it
     cannot disagree with the panel about what is in the basket. */
  function armStickyBuy() {
    document.querySelectorAll('[data-do-sticky-buy]:not([data-do-sticky-bound])').forEach(function (bar) {
      bar.setAttribute('data-do-sticky-bound', '');
      var form = document.querySelector('form.pd__form');
      var real = form && form.querySelector('[data-do-buy-btn]');
      var cta = bar.querySelector('[data-do-sticky-cta]');
      var vslot = bar.querySelector('[data-do-sticky-variant]');
      var sizes = form && form.querySelector('.pd__sizes');
      if (!form || !real || !cta) { bar.remove(); return; }

      function mirror() {
        var state = real.getAttribute('data-state') || '';
        cta.disabled = false;               // never dead: see the click handler
        cta.setAttribute('data-state', state);
        var chosen = form.querySelector('input[type="radio"][name="id"]:checked')
                  || form.querySelector('select[name="id"]');
        var label = '';
        if (chosen && chosen.tagName === 'SELECT') {
          label = chosen.options[chosen.selectedIndex] ? chosen.options[chosen.selectedIndex].text.split(' — ')[0] : '';
        } else if (chosen) {
          label = chosen.nextElementSibling ? chosen.nextElementSibling.textContent.trim() : '';
        }
        if (vslot) vslot.textContent = label;

        /* The price goes on the BAR's button and only the bar's — the wireframe
           reads "Add to bag — $138.00" here. It belongs here and not on the
           panel's button because by the time this bar is on screen the price in
           the buy column has scrolled away; it is the only price the shopper can
           still see. The panel's own button keeps its plain label, one line
           under a price that is right there. */
        var price = state === 'add' && chosen && chosen.getAttribute
          ? chosen.getAttribute('data-price') : '';
        cta.textContent = price ? real.textContent + ' — ' + price : real.textContent;
      }

      /* The bar's button is never disabled even when the real one is. A
         disabled control on a sticky bar is a dead end on the surface the
         shopper is most likely to tap; here it becomes the shortcut to the
         thing they still have to do, which is choose a size. */
      cta.addEventListener('click', function () {
        var chosen = form.querySelector('input[type="radio"][name="id"]:checked');
        var hasSelect = form.querySelector('select[name="id"]');
        if (!chosen && !hasSelect) {
          if (sizes) {
            sizes.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
            var first = sizes.querySelector('input:not(:disabled)');
            if (first) window.setTimeout(function () { first.focus(); }, reduce ? 0 : 380);
          }
          return;
        }
        if (real.disabled) {
          if (sizes) sizes.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
          return;
        }
        real.click();
      });

      form.addEventListener('change', mirror);
      // armBuy() repaints the real button's text on its own change handler, and
      // handler order between two listeners on the same event is not something
      // to rely on — read it back a tick later instead of racing it.
      form.addEventListener('change', function () { window.setTimeout(mirror, 0); });
      mirror();

      /* Reveal is a POSITION test on a scroll listener, not an
         IntersectionObserver — and that distinction is the whole reason this
         works. The condition here is not "is the button visible", it is "is the
         button ABOVE me", because the bar must appear once the shopper has
         scrolled past the buy panel and must NOT appear while they are still
         above it looking at the photograph. IntersectionObserver reports
         threshold CROSSINGS. On this PDP the button starts below the fold, so a
         single fast scroll takes it from below-the-viewport straight to
         above-the-viewport; it never intersects, no crossing occurs, and the
         callback never fires at all. Measured: one delivery, `isIntersecting:
         false, top: -900` — the initial one — and nothing after.

         A rAF-coalesced passive scroll listener has no crossing semantics to
         get wrong and costs one getBoundingClientRect per frame the shopper is
         actually scrolling. */
      var ticking = false;
      function sync() {
        ticking = false;
        var show = real.getBoundingClientRect().bottom < 0;
        if (show === !bar.hidden) return;          // no DOM write when unchanged
        bar.hidden = !show;
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (show) requestAnimationFrame(function () { bar.setAttribute('data-open', ''); });
        else bar.removeAttribute('data-open');
      }
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(sync);
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      sync();
    });
  }

  /* --- Predictive search --------------------------------------------------
     The overlay is bound here, but the RESULTS are not built here: every
     response is a rendered `do-predictive-search` section, so prices, sold-out
     states and compare-at treatment are the ones Liquid produced. Nothing in
     this file formats money, and that is the point — `/search/suggest.json`
     would have handed back price strings this script had to re-format, which is
     wrong the moment the shop sells in a second currency.

     Three failure modes are handled explicitly, because each of them looks like
     "search is broken" rather than like itself:

     · A slow response for "sho" landing after a fast one for "shorts" would
       overwrite the right answer with a stale one. Every request carries a
       sequence number and a response whose number is not the current one is
       dropped on the floor, in addition to being aborted.
     · An announcement per keystroke reads the shopper their own typing back as a
       list of products. Only the settled response writes to the status node.
     · A 4xx/5xx (this store rate-limits hard and answers with an HTML challenge
       page) must show a retry, not an empty panel that looks like "no results".
       An empty result set and a failed request are different words on screen. */
  var srOpener = null;
  var srSeq = 0;
  var srTimer = null;
  var srCtrl = null;
  var srActive = -1;
  var SR_DEBOUNCE = 140;   // spec asks 100-180ms; 140 is the middle of it.
  var SR_RECENT_KEY = 'do:recent-searches';

  function srEl() { return document.querySelector('[data-do-search]'); }
  function srIn() { var o = srEl(); return o && o.querySelector('[data-do-search-input]'); }
  function srBody() { var o = srEl(); return o && o.querySelector('[data-do-search-results]'); }

  function srRecentRead() {
    try { return JSON.parse(window.localStorage.getItem(SR_RECENT_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function srRecentAdd(q) {
    q = (q || '').trim();
    if (!q) return;
    try {
      var list = srRecentRead().filter(function (x) { return x !== q; });
      list.unshift(q);
      window.localStorage.setItem(SR_RECENT_KEY, JSON.stringify(list.slice(0, 5)));
    } catch (e) { /* private mode; recent searches are a convenience, not a feature */ }
  }

  function srPaintRecent() {
    var o = srEl();
    var wrap = o && o.querySelector('[data-do-search-recent]');
    var list = wrap && o.querySelector('[data-do-search-recent-list]');
    if (!wrap || !list) return;
    var items = srRecentRead();
    if (!items.length) { wrap.hidden = true; return; }
    list.innerHTML = '';
    items.forEach(function (q) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'do-ps__chip';
      a.href = '/search?q=' + encodeURIComponent(q);
      a.textContent = q;                       // textContent, never innerHTML: this
      a.setAttribute('data-do-ps-item', '');   // string came from the shopper.
      a.setAttribute('data-do-ps-kind', 'recent');
      li.appendChild(a);
      list.appendChild(li);
    });
    wrap.hidden = false;
  }

  function srItems() {
    var b = srBody();
    return b ? Array.prototype.slice.call(b.querySelectorAll('[data-do-ps-item]')) : [];
  }

  /* A roving virtual cursor. Real focus never leaves the input — move it onto a
     row and the caret goes with it, and the shopper cannot carry on typing. */
  function srMove(delta) {
    var items = srItems();
    var input = srIn();
    if (!items.length || !input) return;
    items.forEach(function (n) { n.removeAttribute('data-do-ps-active'); });
    srActive += delta;
    if (srActive < 0) srActive = items.length - 1;
    if (srActive >= items.length) srActive = 0;
    var el = items[srActive];
    if (!el.id) el.id = 'do-ps-i-' + srActive;
    el.setAttribute('data-do-ps-active', '');
    input.setAttribute('aria-activedescendant', el.id);
    if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }

  function srClearActive() {
    srActive = -1;
    var input = srIn();
    if (input) input.removeAttribute('aria-activedescendant');
    srItems().forEach(function (n) { n.removeAttribute('data-do-ps-active'); });
  }

  function srState(state) {
    var o = srEl();
    if (!o) return;
    o.setAttribute('data-state', state);
    var b = srBody();
    if (b) b.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    var input = srIn();
    if (input) input.setAttribute('aria-expanded', state === 'results' ? 'true' : 'false');
  }

  function srSay(msg) {
    var o = srEl();
    var s = o && o.querySelector('[data-do-search-status]');
    if (s) s.textContent = msg;
  }

  function openSearch() {
    var o = srEl();
    if (!o) return;
    srOpener = document.activeElement;
    o.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    srPaintRecent();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        o.setAttribute('data-open', '');
        var input = srIn();
        if (input) { input.focus(); input.select(); }
      });
    });
    srState(srIn() && srIn().value.trim() ? 'results' : 'idle');
  }

  function closeSearch() {
    var o = srEl();
    if (!o || o.hidden) return;
    if (srCtrl) { try { srCtrl.abort(); } catch (e) {} srCtrl = null; }
    window.clearTimeout(srTimer);
    o.removeAttribute('data-open');
    document.documentElement.style.overflow = '';
    srClearActive();
    var done = function () { o.hidden = true; };
    if (reduce) { done(); } else { window.setTimeout(done, 260); }
    if (srOpener && srOpener.focus) srOpener.focus();
    srOpener = null;
  }

  function srRender(html, q) {
    var b = srBody();
    if (!b) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    var fresh = wrap.querySelector('[data-do-ps]');
    if (!fresh) { srState('error'); srSay('Search is unavailable. Try again.'); return; }
    b.innerHTML = '';
    b.appendChild(fresh);
    srClearActive();
    var n = parseInt(fresh.getAttribute('data-do-ps-count'), 10) || 0;
    srState(n ? 'results' : 'empty');
    srSay(n ? (n + (n === 1 ? ' result for ' : ' results for ') + q) : ('No results for ' + q));
    srTrack('driveon:search', { query: q, results: n });
  }

  function srError(q) {
    var b = srBody();
    if (!b) return;
    b.innerHTML =
      '<div class="do-ps do-ps--error"><p class="do-ps__none">Search could not be reached.</p>' +
      '<button class="do-ps__retry" type="button" data-do-search-retry>Try again</button></div>';
    srState('error');
    srSay('Search could not be reached.');
    srTrack('driveon:search-error', { query: q });
  }

  /* No analytics vendor is wired on this store. Rather than invent a pixel call,
     the events the spec asks to be tracked are dispatched on `document` with the
     payload already assembled, so whichever vendor is installed later listens
     for them and nothing in this file has to change. */
  function srTrack(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) {}
  }

  function srFetch(q) {
    var o = srEl();
    var base = o && o.getAttribute('data-do-search-url');
    if (!base || !window.fetch) return;
    if (srCtrl) { try { srCtrl.abort(); } catch (e) {} }
    srCtrl = window.AbortController ? new window.AbortController() : null;
    var seq = ++srSeq;
    srState('loading');
    var url = base + '?q=' + encodeURIComponent(q) +
      '&section_id=do-predictive-search' +
      '&resources[type]=product,collection,article,page' +
      '&resources[limit]=5' +
      '&resources[options][unavailable_products]=last';
    fetch(url, { credentials: 'same-origin', signal: srCtrl ? srCtrl.signal : undefined })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        // Belt and braces: abort handles the request, the sequence number handles
        // a response that was already in flight when the abort was issued.
        if (seq !== srSeq) return;
        srRender(html, q);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (seq !== srSeq) return;
        srError(q);
      });
  }

  function srInput() {
    var input = srIn();
    var o = srEl();
    if (!input || !o) return;
    var q = input.value.trim();
    var min = parseInt(input.getAttribute('data-do-search-min'), 10) || 2;
    var clear = o.querySelector('[data-do-search-clear]');
    if (clear) clear.hidden = q.length === 0;
    window.clearTimeout(srTimer);
    if (q.length < min) {
      // Below the threshold there is nothing to abort into: cancel the in-flight
      // request too, or a result for "sh" lands on a panel showing the browse
      // list and contradicts it.
      if (srCtrl) { try { srCtrl.abort(); } catch (e) {} srCtrl = null; }
      srSeq++;
      srReset();
      return;
    }
    srState('typing');
    srTimer = window.setTimeout(function () { srFetch(q); }, SR_DEBOUNCE);
  }

  /* The idle panel is server-rendered by the header section and captured here
     once, before the first result set overwrites it. Capturing it beats
     re-rendering it in JavaScript: the browse links come from a real Shopify
     menu, and rebuilding them in this file would mean the script owning markup
     the section already owns and drifting from it the first time the menu
     changes. Clearing the field restores exactly what Liquid printed. */
  var srIdleHTML = null;

  function srReset() {
    var b = srBody();
    if (!b) return;
    if (srIdleHTML !== null) b.innerHTML = srIdleHTML;
    srPaintRecent();
    srClearActive();
    srState('idle');
    srSay('');
  }

  function armSearch() {
    if (document.documentElement.hasAttribute('data-do-search-bound')) return;
    if (!window.fetch) return;   // no fetch, no overlay: the trigger stays a link.
    document.documentElement.setAttribute('data-do-search-bound', '');

    var b0 = srBody();
    if (b0 && srIdleHTML === null) srIdleHTML = b0.innerHTML;

    document.addEventListener('click', function (e) {
      var open = e.target.closest('[data-do-search-open]');
      if (open && srEl()) { e.preventDefault(); openSearch(); return; }
      if (e.target.closest('[data-do-search-close]')) { e.preventDefault(); closeSearch(); return; }
      if (e.target.closest('[data-do-search-clear]')) {
        e.preventDefault();
        var input = srIn();
        if (input) { input.value = ''; input.focus(); }
        var o = srEl();
        var c = o && o.querySelector('[data-do-search-clear]');
        if (c) c.hidden = true;
        srSeq++;
        srReset();
        return;
      }
      if (e.target.closest('[data-do-search-retry]')) {
        e.preventDefault();
        var i2 = srIn();
        if (i2 && i2.value.trim()) srFetch(i2.value.trim());
        return;
      }
      var item = e.target.closest('[data-do-ps-item]');
      if (item && srEl() && !srEl().hidden) {
        var input3 = srIn();
        srRecentAdd(input3 ? input3.value.trim() : '');
        srTrack('driveon:search-select', {
          query: input3 ? input3.value.trim() : '',
          kind: item.getAttribute('data-do-ps-kind') || 'unknown',
          position: srItems().indexOf(item) + 1,
          url: item.getAttribute('href'),
        });
      }
    });

    document.addEventListener('input', function (e) {
      if (e.target.closest('[data-do-search-input]')) srInput();
    });

    document.addEventListener('submit', function (e) {
      if (!e.target.closest('[data-do-search-form]')) return;
      // Not prevented: this is a real GET to /search and the query survives it.
      var input = srIn();
      srRecentAdd(input ? input.value.trim() : '');
    });

    document.addEventListener('keydown', function (e) {
      var o = srEl();
      if (!o || o.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); closeSearch(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); srMove(1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); srMove(-1); return; }
      if (e.key === 'Enter') {
        var items = srItems();
        if (srActive >= 0 && items[srActive]) { e.preventDefault(); items[srActive].click(); }
        return;   // otherwise the form submits to /search, which is correct.
      }
      if (e.key !== 'Tab') return;
      var panel = o.querySelector('[data-do-search-panel]');
      var f = panel && focusables(panel);
      if (!f || !f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function boot() {
    indexStagger();
    armReveal();
    sweepVisible();
    seamMarquees();
    armCounters();
    armParallax();
    armHeader();
    armQuickAdd();
    armShipBars();
    armBuy();
    armStickyBuy();   // after armBuy: it mirrors the button armBuy has painted
    armDrawer();
    armSet();
    armQuickView();
    armQtySteppers();
    armZoom();
    armTransitions();
    armProductForms();
    armQuickViewPrefetch();
    armSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', boot);
  window.addEventListener('resize', sweepVisible, { passive: true });
  window.addEventListener('scroll', sweepVisible, { passive: true, once: true });

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);

  window.DriveOn = { boot: boot, refreshBagCount: refreshBagCount,
                     openDrawer: openDrawer, closeDrawer: closeDrawer,
                     refreshDrawer: refreshDrawer };
})();
